import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import Switch from "../../components/Switch/Switch";
import { getCronJobsApi, toggleCronJobApi } from "../../services/cronJobs";
import { toastMessage } from "../../utils/toastMessage";

const STATUS_LABEL = {
  on: "On",
  off: "Off",
  stopped_in_code: "Stopped in code",
  not_scheduled: "Not scheduled",
};

const STATUS_COLOR = {
  on: { color: "#166534", background: "#dcfce7" },
  off: { color: "#991b1b", background: "#fee2e2" },
  stopped_in_code: { color: "#92400e", background: "#fef3c7" },
  not_scheduled: { color: "#475569", background: "#f1f5f9" },
};

const fmt = (value) => (value ? dayjs(value).format("DD MMM YYYY, hh:mm A") : "—");

const CronJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [toggling, setToggling] = useState("");

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCronJobsApi();
      if (res?.data?.success) {
        setJobs(res.data.result || []);
      } else {
        toastMessage(res?.data?.message || "Could not load cron jobs", "error");
      }
    } catch (error) {
      toastMessage(error?.response?.data?.message || "Could not load cron jobs", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const counts = useMemo(() => {
    return jobs.reduce(
      (total, job) => {
        total[job.status] = (total[job.status] || 0) + 1;
        return total;
      },
      { on: 0, off: 0, stopped_in_code: 0, not_scheduled: 0 }
    );
  }, [jobs]);

  const visibleJobs = useMemo(() => {
    const query = search.trim().toLowerCase();
    return jobs.filter((job) => {
      if (statusFilter !== "all" && job.status !== statusFilter) return false;
      if (!query) return true;
      return [job.title, job.name, job.description, job.schedule]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [jobs, search, statusFilter]);

  const onToggle = async (job, nextEnabled) => {
    const action = nextEnabled ? "turn on" : "turn off";
    const confirmed = window.confirm(
      nextEnabled
        ? `Turn on "${job.title}"? It will start running on its schedule again.`
        : `Turn off "${job.title}"? It will stay off, including after a server restart, until you turn it back on.`
    );
    if (!confirmed) return;

    setToggling(job.name);
    try {
      const res = await toggleCronJobApi(job.name, nextEnabled);
      if (res?.data?.success) {
        setJobs((current) =>
          current.map((item) => (item.name === job.name ? res.data.result : item))
        );
        toastMessage(res.data.message || `Cron job ${action} complete`, "success");
      } else {
        toastMessage(res?.data?.message || "Could not update cron job", "error");
      }
    } catch (error) {
      toastMessage(error?.response?.data?.message || "Could not update cron job", "error");
    } finally {
      setToggling("");
    }
  };

  return (
    <div className="WrapperArea">
      <div className="WrapperBox">
        <div style={{ marginBottom: 16 }}>
          <h4 className="Title" style={{ margin: 0 }}>Cron Jobs</h4>
          <p style={{ margin: "6px 0 0", color: "#64748b", fontSize: 13, maxWidth: 760 }}>
            Every scheduled job is listed here. Turn a job off to stop it. The setting is saved, so it stays off after the server restarts.
          </p>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
          {[
            ["all", "All", jobs.length],
            ["on", "On", counts.on],
            ["off", "Off", counts.off],
            ["stopped_in_code", "Stopped in code", counts.stopped_in_code],
            ["not_scheduled", "Not scheduled", counts.not_scheduled],
          ].map(([key, label, count]) => (
            <button
              key={key}
              type="button"
              onClick={() => setStatusFilter(key)}
              style={{
                border: statusFilter === key ? "1px solid #7c3aed" : "1px solid #e2e8f0",
                background: statusFilter === key ? "#f5f3ff" : "#fff",
                color: "#334155",
                borderRadius: 20,
                padding: "6px 12px",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              {label} ({count})
            </button>
          ))}
        </div>

        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by name or description"
          style={{
            width: "100%",
            maxWidth: 420,
            marginBottom: 16,
            border: "1px solid #e2e8f0",
            borderRadius: 6,
            padding: "8px 12px",
            fontSize: 14,
          }}
        />

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "#64748b", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ padding: "10px 12px" }}>Job</th>
                <th style={{ padding: "10px 12px" }}>Schedule</th>
                <th style={{ padding: "10px 12px" }}>Status</th>
                <th style={{ padding: "10px 12px" }}>Next run</th>
                <th style={{ padding: "10px 12px" }}>On / Off</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} style={{ padding: 24, color: "#64748b" }}>Loading cron jobs...</td>
                </tr>
              ) : visibleJobs.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: 24, color: "#64748b" }}>No cron jobs match this filter.</td>
                </tr>
              ) : (
                visibleJobs.map((job) => {
                  const badge = STATUS_COLOR[job.status] || STATUS_COLOR.not_scheduled;
                  return (
                    <tr key={job.name} style={{ borderBottom: "1px solid #f1f5f9", verticalAlign: "top" }}>
                      <td style={{ padding: "14px 12px", minWidth: 280 }}>
                        <div style={{ fontWeight: 600, color: "#0f172a" }}>{job.title}</div>
                        <div style={{ color: "#64748b", fontSize: 12, marginTop: 2 }}>{job.name}</div>
                        <div style={{ color: "#334155", marginTop: 6, lineHeight: 1.45 }}>{job.description}</div>
                        {job.updatedBy || job.updatedByEmail ? (
                          <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 6 }}>
                            Last changed by {job.updatedBy || job.updatedByEmail}
                            {job.updatedAt ? ` on ${fmt(job.updatedAt)}` : ""}
                          </div>
                        ) : null}
                      </td>
                      <td style={{ padding: "14px 12px", whiteSpace: "nowrap" }}>
                        <div>{job.schedule}</div>
                        {job.cron ? (
                          <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 4 }}>{job.cron}</div>
                        ) : null}
                      </td>
                      <td style={{ padding: "14px 12px" }}>
                        <span style={{ ...badge, borderRadius: 12, padding: "3px 8px", fontSize: 12, fontWeight: 600 }}>
                          {STATUS_LABEL[job.status] || job.status}
                        </span>
                      </td>
                      <td style={{ padding: "14px 12px", whiteSpace: "nowrap", color: "#334155" }}>
                        {job.status === "on" ? fmt(job.nextRunAt) : "—"}
                      </td>
                      <td style={{ padding: "14px 12px" }}>
                        {job.canToggle ? (
                          <span style={{ opacity: toggling === job.name ? 0.5 : 1, pointerEvents: toggling ? "none" : "auto" }}>
                            <Switch
                              checked={job.enabled}
                              onChange={(event, checked) =>
                                onToggle(job, typeof checked === "boolean" ? checked : event.target.checked)
                              }
                            />
                          </span>
                        ) : (
                          <span style={{ color: "#94a3b8", fontSize: 12 }}>Cannot change</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CronJobs;
